import get from 'lodash/get'
import isNil from 'lodash/isNil'

import {
  currentItemToExchangeDetails,
  selectedFitIssueAnswersForCurrentItem,
  selectedIssuesForCurrentItem,
} from '../../store/replacements'
import {
  JUST_RIGHT,
  REPLACEMENTS_FIT_ISSUE,
  ReplacementsItemIssueOptions,
} from '../../store/replacements/enums'
import {JACKETS_CATEGORY_SLUG} from '../../const'
/**
 * Helper function to create a unique ID by concatenating the item's order look product ID and its
 * product set ID. Product sets which can contain multiple items (eg: jacket + pants combination)
 * can sometimes share the same order look product ID. In order to guarantee uniqueness, we're
 * combining both IDs.
 *
 * @param {Number} orderLookProductId
 * @param {String} productSetId
 * @returns {string} Unique ID used to look up items in Redux state
 * @throws {Error} Will throw error if orderLookProductId or productSetId are null or undefined
 */
export const getUniqueIdForItem = ({orderLookProductId, productSetId}) => {
  if (isNil(orderLookProductId)) {
    throw new Error('Unable to create unique ID. Missing order look product ID')
  }

  if (isNil(productSetId)) {
    throw new Error('Unable to create unique ID. Missing product set ID')
  }

  return `${orderLookProductId}:${productSetId}`
}

/**
 * Helper function to determine if current item is eligible for tailoring-only intercept flow. This function is used
 * in the replacements flow reducer logic when submitting the fit questionnaire step.
 *
 * @param {Object} replacementsReduxState Reference to the current replacements flow Redux state
 * @returns {Boolean} Value to determine whether or not item is eligible for tailoring-only intercept flow
 */
export const isCurrentItemToExchangeTailoringOnly = (replacementsReduxState) => {
  // we can take advantage of reusing our selectors by namespacing the sliced state by the 'replacements' key
  const stateForSelectors = {replacements: {...replacementsReduxState}}

  const currentItem = currentItemToExchangeDetails(stateForSelectors)
  if (currentItem.categorySlug !== JACKETS_CATEGORY_SLUG) {
    return false
  }

  const currentItemIssues = selectedIssuesForCurrentItem(stateForSelectors)

  // validate that user has selected fit issue type
  if (!currentItemIssues.includes(ReplacementsItemIssueOptions.indexOf(REPLACEMENTS_FIT_ISSUE))) {
    return false
  }

  // validate that user has only selected the fit issue type
  if (currentItemIssues.length > 1) {
    return false
  }

  // The assumed order of the answers should be in the same order as the categoryMetaDataType mapping for jackets and
  // pants. This also makes the assumption that the number of questions provided for the pants or jackets questionnaire
  // remains static. If this changes in the future, this will need to be updated.
  const {fitQuestionAnswers} = selectedFitIssueAnswersForCurrentItem(stateForSelectors)
  return (
    fitQuestionAnswers[0].selectedFitOption === JUST_RIGHT &&
    fitQuestionAnswers[1].selectedFitOption === JUST_RIGHT &&
    fitQuestionAnswers[2].selectedFitOption !== JUST_RIGHT
  )
}

/**
 * Helper function to format the shipping address retrieved from the fetch order shipping address API into
 * Redux form object with matching field names.
 *
 * @see src/js/components/Forms/AddressForm
 * @param {Object} shippingAddress Object containing shipping address information for user.
 * @param {Object} shippingAddress.shipping_address_1 User's primary street address
 * @param {Object} shippingAddress.shipping_address_2 User's secondary street address
 * @param {Object} shippingAddress.shipping_city User's city address
 * @param {Object} shippingAddress.shipping_first_name First name of user
 * @param {Object} shippingAddress.shipping_last_name Last name of user
 * @param {Object} shippingAddress.shipping_phone User's registered phone number
 * @param {Object} shippingAddress.shipping_state User's state address
 * @param {Object} shippingAddress.shipping_to_business_address Flag to determine if address is a business address
 * @param {Object} shippingAddress.shipping_zip User's zip address
 * @todo Refactor checkout address formatting logic to be reused here
 * @returns {Object} Formatted Redux form shipping address object
 */
export const formatShippingAddress = (shippingAddress) => ({
  address1: get(shippingAddress, 'shipping_address_1'),
  address2: get(shippingAddress, 'shipping_address_2'),
  city: get(shippingAddress, 'shipping_city'),
  firstName: get(shippingAddress, 'shipping_first_name'),
  lastName: get(shippingAddress, 'shipping_last_name'),
  phone: get(shippingAddress, 'shipping_phone'),
  state: get(shippingAddress, 'shipping_state'),
  toBusinessAddress: get(shippingAddress, 'shipping_to_business_address'),
  zip: get(shippingAddress, 'shipping_zip'),
})

const replacementHasMoreThanOneItemToExchange = (state) =>
  state.selectedItemsToExchange.allIds.length > 1
const replacementItemHasMoreThanOneIssue = (state) =>
  state.selectedItemsToExchange.byId[state.selectedItemsToExchange.allIds[0]].issues.length > 1
const userSelectedOnlyGeneralIssueForItem = (state) => {
  const {issues} = state.selectedItemsToExchange.byId[state.selectedItemsToExchange.allIds[0]]
  return issues[0] !== ReplacementsItemIssueOptions.indexOf(REPLACEMENTS_FIT_ISSUE)
}
const userSelectedFitIssueForNonFitItem = (state) => {
  const {issues, issuesByType} =
    state.selectedItemsToExchange.byId[state.selectedItemsToExchange.allIds[0]]
  return (
    issuesByType.general &&
    issues[0] === ReplacementsItemIssueOptions.indexOf(REPLACEMENTS_FIT_ISSUE)
  )
}
const userSelectedNonNeutralFitOptionForItem = (state) => {
  const {issuesByType} = state.selectedItemsToExchange.byId[state.selectedItemsToExchange.allIds[0]]
  return (
    issuesByType.fit &&
    !issuesByType.fit.fitQuestionAnswers.every(
      ({selectedFitOption}) => selectedFitOption === JUST_RIGHT,
    )
  )
}

export const shouldNavigateToAddressForm = (state) =>
  replacementHasMoreThanOneItemToExchange(state) ||
  replacementItemHasMoreThanOneIssue(state) ||
  userSelectedOnlyGeneralIssueForItem(state) ||
  userSelectedFitIssueForNonFitItem(state) ||
  userSelectedNonNeutralFitOptionForItem(state)
